import { createContext, PropsWithChildren, useContext, useEffect, useState } from "react";
import { getAuthToken, setAuthToken, subscribeAuthToken, hydrateAuthTokenFromNative, onAuthLost, syncAuthTokenFromRuntime } from "../../lib/auth";
import { getLocalUser, setLocalUser, disconnectSocket } from "../../lib/socket";
import type { AuthUser } from "../../types/auth";
import { syncAppearanceFromServer } from "../../lib/settings/applyAppearance";

type AuthStatus = "checking" | "authenticated" | "unauthenticated";

type AuthContextValue = {
  token: string | null;
  user: AuthUser | null;
  status: AuthStatus;
  setAuth: (token: string | null, user: AuthUser | null) => void;
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: PropsWithChildren) {
  const initialToken = getAuthToken();
  const [token, setToken] = useState<string | null>(initialToken);
  const [user, setUser] = useState<AuthUser | null>(getLocalUser());
  const [status, setStatus] = useState<AuthStatus>(initialToken ? "authenticated" : "checking");

  // Carry the account's accent/background to this device: once when a session starts,
  // then when the tab regains focus. Skipped on /settings so an unsaved preview isn't overwritten.
  useEffect(() => {
    if (!token) return;
    syncAppearanceFromServer(true).catch(() => {});
    const onVisible = () => {
      if (document.visibilityState !== "visible" || window.location.pathname.startsWith("/settings")) return;
      syncAppearanceFromServer().catch(() => {});
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [token]);

  useEffect(() => {
    let cancelled = false;
    const unsub = subscribeAuthToken((nextToken) => {
      setToken(nextToken);
      setStatus(nextToken ? "authenticated" : "unauthenticated");
    });

    async function hydrate() {
      if (getAuthToken()) {
        if (!cancelled) setStatus("authenticated");
        return;
      }

      const refreshed = await hydrateAuthTokenFromNative().catch(() => null);
      if (cancelled) return;
      if (refreshed || getAuthToken()) {
        setStatus("authenticated");
        return;
      }

      const deadline = Date.now() + 1800;
      while (!cancelled && Date.now() < deadline) {
        const restored = syncAuthTokenFromRuntime();
        if (restored) {
          setToken(restored);
          setStatus("authenticated");
          return;
        }
        await new Promise((resolve) => window.setTimeout(resolve, 120));
      }

      if (!cancelled) setStatus("unauthenticated");
    }

    hydrate();
    const off = onAuthLost(() => {
      setAuth(null, null);
      setStatus("unauthenticated");
    });
    return () => { cancelled = true; unsub(); off(); };
  }, []);

  const setAuth = (newToken: string | null, newUser: AuthUser | null) => {
    // If a different user is logging in, wipe all previous user's cached data
    // so their spaces/messages/channels never flash onto the new session.
    const prevUser = getLocalUser();
    const userChanged = newToken && newUser && prevUser && prevUser.id !== newUser.id;
    if (userChanged || !newToken) {
      const keysToRemove = [
        '__echo_channel_snapshots__',
        'currentVoidId',
        'currentChannelId',
        'chanOrder',
        'favorites',
        'outbox',
        'hiddenSpaces',
        'mutedSpaces',
        'chanW',
        'peopleW',
        'me',
      ];
      keysToRemove.forEach(k => { try { localStorage.removeItem(k); } catch {} });
    }
    setAuthToken(newToken);
    setLocalUser(newUser);
    setToken(newToken);
    setUser(newUser);
    setStatus(newToken ? "authenticated" : "unauthenticated");
    if (!newToken) disconnectSocket();
  };

  return <AuthContext.Provider value={{ token, user, status, setAuth }}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
